import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { 
  Plus, Search, Filter, Calendar, Clock, 
  CheckCircle, AlertCircle, ChevronDown,
  Flag, MoreVertical, Users, RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import AddTaskModal from '../../components/modals/AddTaskModal';
import { 
  isGoogleTasksAuthorized, 
  getGoogleTasksCredentials, 
  fetchGoogleTaskLists, 
  fetchGoogleTasks, 
  createGoogleTask, 
  authenticateGoogleTasks,
  handleGoogleTasksCallback
} from '../../lib/tasks/googleTasks';

interface Task {
  id: string;
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'completed';
  due_date: string;
  created_at: string;
  updated_at: string;
  category?: string;
  assignee?: string;
  source?: string;
  source_id?: string;
}

interface TaskFormData {
  title: string;
  description: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
  assignee: string;
}

const Tasks: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [googleTaskLists, setGoogleTaskLists] = useState<any[]>([]);
  const [selectedTaskList, setSelectedTaskList] = useState<string | null>(null);
  const [isSyncingGoogle, setIsSyncingGoogle] = useState(false);
  const queryClient = useQueryClient();

  // Check if Google Tasks is connected
  useEffect(() => {
    const checkGoogleTasks = async () => {
      try {
        const isAuthorized = await isGoogleTasksAuthorized();
        setIsGoogleConnected(isAuthorized);
        
        if (isAuthorized) {
          const credentials = await getGoogleTasksCredentials();
          if (credentials?.access_token) {
            const taskLists = await fetchGoogleTaskLists(credentials.access_token);
            setGoogleTaskLists(taskLists);
            if (taskLists.length > 0) {
              setSelectedTaskList(taskLists[0].id);
            }
          }
        }
      } catch (error) {
        console.error('Error checking Google Tasks:', error);
      }
    };
    
    checkGoogleTasks();
  }, []);

  // Check for Google Tasks callback
  useEffect(() => {
    // Check if we're returning from a Google OAuth redirect with a code
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code && window.location.pathname.includes('/dashboard/tasks')) {
      // Process the OAuth callback
      handleGoogleTasksCallback(window.location.href)
        .then(() => {
          // Refresh the page to show the connected state
          window.location.href = '/dashboard/tasks';
        })
        .catch(error => {
          console.error('Error handling Google Tasks callback:', error);
          toast.error('Failed to connect Google Tasks');
        });
    }
  }, []);

  // Fetch tasks from Supabase
  const { data: localTasks, isLoading: isLoadingLocal } = useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      return data as Task[];
    }
  });

  // Fetch tasks from Google Tasks
  const { data: googleTasks, isLoading: isLoadingGoogle, refetch: refetchGoogleTasks } = useQuery({
    queryKey: ['googleTasks', selectedTaskList],
    queryFn: async () => {
      if (!isGoogleConnected || !selectedTaskList) {
        return [];
      }
      
      const credentials = await getGoogleTasksCredentials();
      if (!credentials?.access_token) {
        return [];
      }
      
      const tasks = await fetchGoogleTasks(credentials.access_token, selectedTaskList);
      
      // Transform Google Tasks to match our Task interface
      return tasks.map(task => ({
        id: task.id,
        title: task.title,
        description: task.notes,
        priority: 'medium' as const, // Google Tasks doesn't have priority
        status: task.status === 'completed' ? 'completed' as const : 'pending' as const,
        due_date: task.due || new Date().toISOString(),
        created_at: task.updated,
        updated_at: task.updated,
        source: 'google',
        source_id: task.id
      }));
    },
    enabled: isGoogleConnected && !!selectedTaskList
  });

  // Combine local and Google tasks
  const allTasks = React.useMemo(() => {
    const local = localTasks || [];
    const google = googleTasks || [];
    
    // Avoid duplicates by checking source_id
    const googleTaskIds = new Set(google.map(task => task.source_id));
    const filteredLocal = local.filter(task => !task.source_id || !googleTaskIds.has(task.source_id));
    
    return [...filteredLocal, ...google];
  }, [localTasks, googleTasks]);

  // Create task mutation for local database
  const createTask = useMutation({
    mutationFn: async (taskData: TaskFormData) => {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          title: taskData.title,
          description: taskData.description,
          due_date: taskData.dueDate,
          priority: taskData.priority,
          status: 'pending',
          category: taskData.category,
          assignee: taskData.assignee
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      
      // If Google Tasks is connected, also create the task there
      if (isGoogleConnected && selectedTaskList) {
        createGoogleTaskFromLocal(data);
      } else {
        toast.success('Task created successfully');
        setIsAddModalOpen(false);
      }
    },
    onError: (error) => {
      toast.error(`Failed to create task: ${error.message}`);
    }
  });

  // Create task in Google Tasks
  const createGoogleTaskFromLocal = async (localTask: Task) => {
    try {
      const credentials = await getGoogleTasksCredentials();
      if (!credentials?.access_token || !selectedTaskList) {
        throw new Error('Google Tasks not properly configured');
      }
      
      // Create task in Google Tasks
      const googleTask = await createGoogleTask(
        credentials.access_token,
        selectedTaskList,
        {
          title: localTask.title,
          notes: localTask.description,
          due: localTask.due_date
        }
      );
      
      // Update local task with Google Task ID
      const { error } = await supabase
        .from('tasks')
        .update({
          source: 'google',
          source_id: googleTask.id
        })
        .eq('id', localTask.id);
      
      if (error) throw error;
      
      // Refetch tasks
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['googleTasks'] });
      
      toast.success('Task created and synced with Google Tasks');
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error creating Google Task:', error);
      toast.error('Task created locally but failed to sync with Google Tasks');
      setIsAddModalOpen(false);
    }
  };

  // Update task status mutation
  const updateTaskStatus = useMutation({
    mutationFn: async ({ taskId, status }: { taskId: string; status: Task['status'] }) => {
      const { data, error } = await supabase
        .from('tasks')
        .update({ status })
        .eq('id', taskId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task status updated');
    },
    onError: (error) => {
      toast.error(`Failed to update task: ${error.message}`);
    }
  });

  const handleSaveTask = async (taskData: TaskFormData) => {
    await createTask.mutate(taskData);
  };

  const syncGoogleTasks = async () => {
    try {
      setIsSyncingGoogle(true);
      await refetchGoogleTasks();
      toast.success('Google Tasks synced successfully');
    } catch (error) {
      console.error('Error syncing Google Tasks:', error);
      toast.error('Failed to sync Google Tasks');
    } finally {
      setIsSyncingGoogle(false);
    }
  };

  const handleConnectGoogleTasks = async () => {
    try {
      await authenticateGoogleTasks();
    } catch (error) {
      console.error('Error connecting to Google Tasks:', error);
      toast.error('Failed to connect to Google Tasks');
    }
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-green-600 bg-green-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'in-progress':
        return 'text-blue-600 bg-blue-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const stats = [
    {
      label: 'Total Tasks',
      value: allTasks?.length || 0,
      icon: <CheckCircle className="h-5 w-5 text-primary" />
    },
    {
      label: 'Due Today',
      value: allTasks?.filter(task => {
        const today = new Date().toISOString().split('T')[0];
        return task.due_date.split('T')[0] === today;
      }).length || 0,
      icon: <Calendar className="h-5 w-5 text-warning" />
    },
    {
      label: 'Completed',
      value: allTasks?.filter(task => task.status === 'completed').length || 0,
      icon: <CheckCircle className="h-5 w-5 text-success" />
    },
    {
      label: 'High Priority',
      value: allTasks?.filter(task => task.priority === 'high').length || 0,
      icon: <Flag className="h-5 w-5 text-error" />
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Track and manage your tasks and deadlines
          </p>
        </div>
        <div className="flex gap-2">
          {isGoogleConnected ? (
            <button 
              className="btn btn-outline flex items-center"
              onClick={syncGoogleTasks}
              disabled={isSyncingGoogle}
            >
              {isSyncingGoogle ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing Google Tasks...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Google Tasks
                </>
              )}
            </button>
          ) : (
            <button 
              className="btn btn-outline flex items-center"
              onClick={handleConnectGoogleTasks}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Connect Google Tasks
            </button>
          )}
          <button 
            className="btn btn-primary flex items-center"
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-gray-50 rounded-lg">
                {stat.icon}
              </div>
            </div>
            <h3 className="text-2xl font-bold mt-4">{stat.value}</h3>
            <p className="text-gray-600">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary appearance-none"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Tasks</option>
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            {isGoogleConnected && googleTaskLists.length > 0 && (
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary appearance-none"
                  value={selectedTaskList || ''}
                  onChange={(e) => setSelectedTaskList(e.target.value)}
                >
                  <option value="">All Task Lists</option>
                  {googleTaskLists.map(list => (
                    <option key={list.id} value={list.id}>{list.title}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Task
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoadingLocal || isLoadingGoogle ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                    Loading tasks...
                  </td>
                </tr>
              ) : allTasks && allTasks.length > 0 ? (
                allTasks
                  .filter(task => {
                    // Filter by search term
                    const matchesSearch = searchTerm === '' || 
                      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (task.description || '').toLowerCase().includes(searchTerm.toLowerCase());
                    
                    // Filter by status
                    const matchesStatus = filter === 'all' || task.status === filter;
                    
                    return matchesSearch && matchesStatus;
                  })
                  .map((task) => (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {task.title}
                          </div>
                          {task.description && (
                            <div className="text-sm text-gray-500">
                              {task.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(task.due_date), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={task.status}
                          onChange={(e) => updateTaskStatus.mutate({ 
                            taskId: task.id, 
                            status: e.target.value as Task['status'] 
                          })}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <Users className="h-4 w-4 text-gray-500" />
                          </div>
                          <span className="ml-2 text-sm text-gray-900">
                            {task.assignee || 'Unassigned'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {task.source === 'google' ? 'Google Tasks' : 'AdvisorGrow'}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <button className="text-gray-400 hover:text-gray-500">
                          <MoreVertical className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No tasks found. Create your first task to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddTaskModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveTask}
      />
    </div>
  );
};

export default Tasks;